'use client';

import { useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useWbsStore } from '@/stores/wbsStore';
import type { Task } from '@/types';
import {
  DAY_WIDTH,
  generateProjectDates,
  getDatesByMonth,
  getDatesByWeek,
  getDateOffset,
  getBarWidth,
  getGanttTotalWidth,
  isWeekend,
  isToday,
  getStatusClass,
  formatDate,
} from '@/lib/gantt-utils';

interface GanttChartProps {
  onEditTask: (task: Task) => void;
  projectStart?: string;
  projectEnd?: string;
}

export default function GanttChart({ onEditTask, projectStart, projectEnd }: GanttChartProps) {
  const { tasks } = useWbsStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; task: Task | null }>({
    visible: false,
    x: 0,
    y: 0,
    task: null,
  });

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => a.order_index - b.order_index);
  }, [tasks]);

  // 프로젝트 시작/종료일 계산 (props 우선, tasks에서 추출 fallback)
  const { PROJECT_START, PROJECT_END } = useMemo(() => {
    // props로 전달된 경우 사용 (프로젝트 기간)
    if (projectStart && projectEnd) {
      return {
        PROJECT_START: dayjs(projectStart),
        PROJECT_END: dayjs(projectEnd),
      };
    }

    // tasks에서 최소/최대 날짜 추출 (날짜가 있는 태스크만)
    const tasksWithDates = tasks.filter(t => t.start_date && t.end_date);
    if (tasksWithDates.length > 0) {
      let minDate = dayjs(tasksWithDates[0].start_date);
      let maxDate = dayjs(tasksWithDates[0].end_date);

      tasksWithDates.forEach(task => {
        const start = dayjs(task.start_date);
        const end = dayjs(task.end_date);
        if (start.isBefore(minDate)) minDate = start;
        if (end.isAfter(maxDate)) maxDate = end;
      });

      return {
        PROJECT_START: minDate,
        PROJECT_END: maxDate,
      };
    }

    // 기본값: 현재 날짜부터 6개월
    const today = dayjs();
    return {
      PROJECT_START: today,
      PROJECT_END: today.add(6, 'month'),
    };
  }, [projectStart, projectEnd, tasks]);

  const totalWidth = useMemo(() => getGanttTotalWidth(PROJECT_START, PROJECT_END), [PROJECT_START, PROJECT_END]);
  const dates = useMemo(() => generateProjectDates(PROJECT_START, PROJECT_END), [PROJECT_START, PROJECT_END]);
  const months = useMemo(() => getDatesByMonth(PROJECT_START, PROJECT_END), [PROJECT_START, PROJECT_END]);
  const weeks = useMemo(() => getDatesByWeek(PROJECT_START, PROJECT_END), [PROJECT_START, PROJECT_END]);

  const handleBarMouseEnter = (e: React.MouseEvent, task: Task) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      task,
    });
  };

  const handleBarMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, task: null });
  };

  const handleBarClick = (task: Task) => {
    onEditTask(task);
  };

  const today = dayjs();
  const isTodayInRange = today.isAfter(PROJECT_START.subtract(1, 'day')) && today.isBefore(PROJECT_END.add(1, 'day'));

  // 프로젝트 기간이 없으면 빈 상태 표시
  if (tasks.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>WBS 일정이 없습니다</p>
          <p className="text-sm text-gray-400 mt-1">프로젝트에 작업을 추가해주세요</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div ref={containerRef} className="flex-1 overflow-auto">
        {/* Header - 총 높이 80px (테이블 헤더와 동일: 28px + 26px + 26px = 80px) */}
        <div className="sticky top-0 bg-gray-50 z-10">
          {/* 1행: 년/월 */}
          <div className="flex border-b border-gray-200" style={{ width: totalWidth, height: '28px' }}>
            {Object.values(months).map((month, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 flex items-center justify-center text-sm font-semibold text-gray-700 border-r border-gray-200 bg-gray-100"
                style={{ width: month.days.length * DAY_WIDTH }}
              >
                {month.name}
              </div>
            ))}
          </div>

          {/* 2행: 주 */}
          <div className="flex border-b border-gray-200" style={{ width: totalWidth, height: '26px' }}>
            {Object.values(weeks).map((week, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 flex items-center justify-center text-xs text-gray-600 border-r border-gray-200 bg-gray-50"
                style={{ width: week.days.length * DAY_WIDTH }}
              >
                {week.week}주
              </div>
            ))}
          </div>

          {/* 3행: 일 */}
          <div className="flex border-b border-gray-200" style={{ width: totalWidth, height: '26px' }}>
            {dates.map((date, idx) => (
              <div
                key={idx}
                className={`flex-shrink-0 flex items-center justify-center text-xs border-r border-gray-200 ${
                  isWeekend(date) ? 'bg-[#fef3c7] text-[#92400e]' : 'bg-white text-gray-500'
                } ${isToday(date) ? 'bg-[#dbeafe] text-[#1d4ed8] font-semibold' : ''}`}
                style={{ width: DAY_WIDTH }}
              >
                {date.date()}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div
          className="relative"
          style={{ width: totalWidth, height: sortedTasks.length * 48 }}
        >
          {/* Weekend backgrounds */}
          {dates.map((date, idx) => (
            isWeekend(date) && (
              <div
                key={`weekend-${idx}`}
                className="absolute bg-amber-50/50"
                style={{
                  left: idx * DAY_WIDTH,
                  top: 0,
                  width: DAY_WIDTH,
                  height: '100%',
                }}
              />
            )
          ))}

          {/* Today column highlight */}
          {isTodayInRange && (
            <div
              className="absolute bg-blue-100/30"
              style={{
                left: getDateOffset(today, PROJECT_START),
                top: 0,
                width: DAY_WIDTH,
                height: '100%',
              }}
            />
          )}

          {/* Task rows and bars */}
          {sortedTasks.map((task, rowIndex) => {
            // 날짜가 없는 태스크는 바 없이 행만 표시
            const hasValidDates = task.start_date && task.end_date;
            const startDate = hasValidDates ? dayjs(task.start_date) : null;
            const endDate = hasValidDates ? dayjs(task.end_date) : null;
            const isInRange = hasValidDates && startDate && endDate
              ? !(endDate.isBefore(PROJECT_START) || startDate.isAfter(PROJECT_END))
              : false;
            const offset = hasValidDates ? getDateOffset(task.start_date!, PROJECT_START) : 0;
            const width = hasValidDates ? getBarWidth(task.start_date!, task.end_date!) : 0;

            return (
              <div key={task.id}>
                {/* Row background */}
                <div
                  className="absolute border-b border-gray-100 hover:bg-gray-50/50"
                  style={{
                    top: rowIndex * 48,
                    width: totalWidth,
                    height: 48,
                  }}
                />

                {/* Task bar - 날짜가 있는 경우만 표시 */}
                {isInRange && (
                  <div
                    className={`absolute rounded cursor-pointer transition-all hover:opacity-80 flex items-center justify-center text-white text-sm font-medium gantt-bar-${getStatusClass(task.status)}`}
                    style={{
                      left: Math.max(0, offset),
                      top: rowIndex * 48 + 10,
                      width: Math.max(width, 0),
                      height: 28,
                    }}
                    onMouseEnter={(e) => handleBarMouseEnter(e, task)}
                    onMouseLeave={handleBarMouseLeave}
                    onClick={() => handleBarClick(task)}
                  >
                    {width > 50 && `${task.progress}%`}
                  </div>
                )}
              </div>
            );
          })}

          {/* Today marker line */}
          {isTodayInRange && (
            <div
              className="absolute top-0 w-0.5 bg-red-500 z-10"
              style={{
                left: getDateOffset(today, PROJECT_START) + DAY_WIDTH / 2,
                height: '100%',
              }}
            />
          )}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip.visible && tooltip.task && (
        <div
          className="fixed z-50 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="font-bold mb-1">{tooltip.task.task_name}</div>
          <div className="text-xs space-y-0.5">
            <div>{tooltip.task.start_date ? formatDate(tooltip.task.start_date, 'YYYY.MM.DD') : '-'} ~ {tooltip.task.end_date ? formatDate(tooltip.task.end_date, 'YYYY.MM.DD') : '-'}</div>
            <div>진행: {tooltip.task.status} | 공정율: {tooltip.task.progress}%</div>
            {tooltip.task.manager && <div>담당: {tooltip.task.manager}</div>}
          </div>
        </div>
      )}

      <style jsx>{`
        .gantt-bar-before {
          background-color: #9ca3af;
        }
        .gantt-bar-progress {
          background-color: #3b82f6;
        }
        .gantt-bar-confirm {
          background-color: #f97316;
        }
        .gantt-bar-complete {
          background-color: #22c55e;
        }
      `}</style>
    </div>
  );
}
